export default class Query {
  response: Promise<any>;

  constructor(uri: string, data?: any, method?: string, options?: RequestInit) {
    const isGet = method?.toUpperCase() === 'GET';
    
    // Add fetch timeout to prevent hanging requests
    const fetchWithTimeout = (url: string, options: RequestInit, timeout = 30000): Promise<Response> => {
      return new Promise((resolve, reject) => {
        // Set timeout to abort fetch if it takes too long
        const controller = new AbortController();
        const timeoutId = setTimeout(() => {
          controller.abort();
          reject(new Error(`Request timeout for ${url}`));
        }, timeout);
        
        // Merge the abort controllers if one was already provided
        const mergedOptions = {...options};
        if (options.signal) {
          // This is a simple approach and doesn't handle all edge cases
          // A more robust solution would listen to abort events on both controllers
          const originalSignal = options.signal;
          originalSignal.addEventListener('abort', () => controller.abort());
        }
        
        // Use our controller's signal
        mergedOptions.signal = controller.signal;
        
        fetch(url, mergedOptions)
          .then(resolve)
          .catch(reject)
          .finally(() => clearTimeout(timeoutId));
      });
    };
    
    // Use enhanced fetch with timeout
    this.response = fetchWithTimeout(uri, {
      method: method || 'POST',
      body: (!isGet && data) ? JSON.stringify(data) : undefined,
      headers: {
        'Accept': 'application/json',
        ...(isGet ? {} : { 'Content-Type': 'application/json' })
      },
      ...options
    }, 30000).then(async res => {
      try {
        const text = await res.text();
        
        if (!res.ok) {
          throw new Error(`API Error (${res.status}): ${text}`);
        }
        
        try {
          return JSON.parse(text);
        } catch (e) {
          console.error('Failed to parse JSON:', text);
          throw new Error(`Invalid JSON response: ${text}`);
        }
      } catch (error) {
        // Handle network errors or timeouts
        if (error && typeof error === 'object' && 'name' in error && (error as any).name === 'AbortError') {
          throw new Error('Request was aborted');
        }
        // Re-throw other errors
        throw error;
      }
    });
  }
} 